import { gunzipSync, gzipSync } from 'fflate'
import {
  computed,
  inject,
  type InjectionKey,
  onMounted,
  ref,
  type Ref,
  shallowRef,
  type ShallowRef,
  watch,
} from 'vue'

const PROTECTION_FIELD = '__pageRenderProtection'
const PROTECTION_VERSION = 3
const PROTECTION_ENCODING = 'gzip-base64-json'

export const PAGE_RENDER_PROTECTION_STATE_KEY: InjectionKey<Readonly<Ref<boolean>>>
  = Symbol('page-render-protection-state')

// A local transparent asset keeps image nodes and their dimensions in the SSR
// tree without making an external request or exposing the original URL.
export const PAGE_RENDER_IMAGE_PLACEHOLDER
  = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%221%22 height=%221%22 viewBox=%220 0 1 1%22%3E%3C/svg%3E'

interface PageRenderProtectionEnvelope {
  version: number
  encoding: typeof PROTECTION_ENCODING
  payload: string
}

type PageRenderProtectionBootstrapResult =
  | { ok: true, value: unknown }
  | { ok: false }

interface PageRenderProtectionBootstrapState {
  tasks: Map<string, Promise<PageRenderProtectionBootstrapResult>>
  startedAt: number
}

declare global {
  interface Window {
    __pageRenderProtectionBootstrap?: PageRenderProtectionBootstrapState
  }
}

export type ProtectedPageRenderData<T extends Record<string, unknown> = Record<string, unknown>> = T & {
  [PROTECTION_FIELD]?: PageRenderProtectionEnvelope
}

interface MaskContext {
  dynamicContent: boolean
}

interface ProtectedPageRenderState<T> {
  data: ShallowRef<T | null | undefined>
  isProtected: Ref<boolean>
  renderRevision: Ref<number>
}

const DYNAMIC_CONTENT_KEYS = new Set([
  'allArcListMap',
  'allGoodsListMap',
  'bindData',
])

const STRUCTURAL_DYNAMIC_KEYS = /^(?:.*(?:Ids?|Key|Type)|width|height|total|totalRecord|pageNo|pageSize|length|sort|index)$/i
const STRUCTURAL_STRING_KEYS = /^(?:categoryTitle|defaultCategorys|mergeFetchData|platformType|justifyContent|alignContent|alignItems|justifyItems|placeContent|placeItems|textAlign|textDecoration|textTransform|textOverflow|textWrap|whiteSpace|wordBreak|overflowWrap|writingMode|className|classStyle|componentName|tagName|slotName|eventName|animationName)$/i
const CONTENT_NUMBER_KEYS = /price|amount|discount|score|rate|rating|sales|sold|money|cost/i
const IMAGE_KEYS = /^src$|srcSet|image|img|pic|photo|logo|iconUrl|avatar|cover|thumbnail|banner|poster|backgroundImage/i
const TEXT_KEYS = /text|title|name|label|description|content|html|remark|message|subtitle|caption|placeholder|alt|tip|notice|summary|footerInfo|mark/i
const HTML_KEYS = /html|content|slot/i
const LINK_KEYS = /^(?:href|link|jumpLink|targetUrl|redirectUrl)$/i
const JSON_STRING_KEYS = /(?:json|staticData|pageData)$/i
const CSS_TEXT_KEYS = /^cssText$/i
const EXECUTABLE_TEXT_KEYS = /^javascriptText$/i
const IMAGE_VALUE = /^data:image\/|\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$|\/imgs?\/|\/images?\/|oss-|imageMogr/i

function placeholderText(value: string): string {
  if (!value)
    return value

  return Array.from(value, (character) => {
    if (character === '\n' || character === '\r' || character === '\t' || character === ' ') {
      return character
    }
    // Full block glyphs merge into the skeleton background without exposing
    // the original copy or collapsing the text node's approximate width.
    return '\u2588'
  }).join('')
}

function maskHtml(value: string): string {
  // v-html is an opaque content boundary. Keeping its original tags would
  // expose element names, attributes and URLs even after the visible copy was
  // masked. Retain only rough line/media occupancy using markup generated by
  // us, while the complete HTML remains exclusively in the encrypted payload.
  const plainSkeleton = value
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<(?:script|style)\b[^>]*>[\s\S]*?<\/(?:script|style)\s*>/gi, ' ')
    .replace(/<(?:br|hr)\b[^>]*>/gi, '\n')
    .replace(
      /<\/(?:address|article|aside|blockquote|dd|div|dl|dt|figcaption|figure|footer|form|h[1-6]|header|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)\s*>/gi,
      '\n',
    )
    .replace(/<(?:canvas|iframe|img|picture|svg|video)\b[^>]*>/gi, '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588')
    .replace(/<[^>]*>/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n[\t ]*\n[\t ]*\n+/g, '\n\n')
    .trim()

  const masked = placeholderText(plainSkeleton || '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588')
  return masked.replace(/\n/g, '<br>')
}

function maskCss(value: string): string {
  return value
    // Page-level CSS controls the original component width, spacing and
    // responsive structure, so keep declarations intact while hiding media.
    .replace(/url\(["']?(.*?)["']?\)/gi, `url(${PAGE_RENDER_IMAGE_PLACEHOLDER})`)
    // Pseudo-element copy is visible page content and must not remain in the
    // protected SSR HTML even though the surrounding CSS rule is structural.
    .replace(
      /(\bcontent\s*:\s*)(["'])(.*?)(\2)/gi,
      (_match, prefix: string, quote: string, content: string) =>
        `${prefix}${quote}${placeholderText(content)}${quote}`,
    )
}

function tryMaskJsonString(value: string, key: string, context: MaskContext): string | null {
  if (!JSON_STRING_KEYS.test(key) || !/^\s*[[{]/.test(value))
    return null

  try {
    return JSON.stringify(maskPageRenderNode(JSON.parse(value), key, context, new WeakMap()))
  }
  catch {
    return null
  }
}

function maskString(value: string, key: string, context: MaskContext): string {
  if (!value)
    return value

  const maskedJson = tryMaskJsonString(value, key, context)
  if (maskedJson !== null)
    return maskedJson

  if (CSS_TEXT_KEYS.test(key))
    return maskCss(value)
  // Never place masked characters into an executable script. The original
  // script remains inside the encrypted envelope and is restored client-side.
  if (EXECUTABLE_TEXT_KEYS.test(key))
    return ''

  // These values participate in SSR cache/request-key lookups. Changing them
  // would remove whole product/article branches from the placeholder tree.
  if (STRUCTURAL_DYNAMIC_KEYS.test(key))
    return value
  if (!context.dynamicContent && STRUCTURAL_STRING_KEYS.test(key))
    return value
  if (LINK_KEYS.test(key))
    return '#'
  if (IMAGE_KEYS.test(key) || (key.toLowerCase() === 'url' && IMAGE_VALUE.test(value))) {
    return PAGE_RENDER_IMAGE_PLACEHOLDER
  }
  if (HTML_KEYS.test(key) && /<[^>]+>/.test(value))
    return maskHtml(value)
  if (context.dynamicContent || TEXT_KEYS.test(key))
    return placeholderText(value)

  return value
}

function maskPageRenderNode(
  value: unknown,
  key: string,
  context: MaskContext,
  seen: WeakMap<object, unknown>,
): unknown {
  if (typeof value === 'string')
    return maskString(value, key, context)
  if (typeof value === 'number') {
    if (context.dynamicContent && CONTENT_NUMBER_KEYS.test(key) && !STRUCTURAL_DYNAMIC_KEYS.test(key)) {
      return 0
    }
    return value
  }
  if (value === null || typeof value !== 'object')
    return value

  if (seen.has(value))
    return seen.get(value)

  if (Array.isArray(value)) {
    const result: unknown[] = []
    seen.set(value, result)
    value.forEach((item) => {
      result.push(maskPageRenderNode(item, key, context, seen))
    })
    return result
  }

  const source = value as Record<string, unknown>
  const result: Record<string, unknown> = {}
  seen.set(value, result)

  for (const [childKey, childValue] of Object.entries(source)) {
    if (childKey === PROTECTION_FIELD)
      continue

    // Translation keys themselves contain the original copy, so an empty map
    // is safer than masking only the values. Text components still render their
    // same-size placeholder from the masked config value.
    if (childKey === 'langJson') {
      result[childKey] = {}
      continue
    }

    result[childKey] = maskPageRenderNode(
      childValue,
      childKey,
      {
        dynamicContent: context.dynamicContent || DYNAMIC_CONTENT_KEYS.has(childKey),
      },
      seen,
    )
  }

  return result
}

export function createPageRenderSkeleton<T>(data: T): T {
  return maskPageRenderNode(
    data,
    '',
    { dynamicContent: false },
    new WeakMap(),
  ) as T
}

function bytesToBinaryString(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length))
    binary += String.fromCharCode(...chunk)
  }

  return binary
}

function binaryStringToBytes(binary: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

function bytesToBase64(bytes: Uint8Array): string {
  return btoa(bytesToBinaryString(bytes))
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  return binaryStringToBytes(atob(value))
}

function encodePageRenderData(data: unknown): PageRenderProtectionEnvelope {
  const plainText = new TextEncoder().encode(JSON.stringify(data))
  const compressed = gzipSync(plainText, { level: 9 })

  return {
    version: PROTECTION_VERSION,
    encoding: PROTECTION_ENCODING,
    payload: bytesToBase64(compressed),
  }
}

function decodePageRenderEnvelope<T>(envelope: PageRenderProtectionEnvelope): T {
  if (envelope.version !== PROTECTION_VERSION || envelope.encoding !== PROTECTION_ENCODING) {
    throw new Error(`Unsupported PageRender protection envelope version: ${envelope.version}`)
  }

  const plainText = gunzipSync(base64ToBytes(envelope.payload))

  return JSON.parse(new TextDecoder().decode(plainText)) as T
}

async function restoreFromEarlyBootstrap(
  envelope: PageRenderProtectionEnvelope,
): Promise<PageRenderProtectionBootstrapResult | null> {
  if (!import.meta.client)
    return null

  const task = window.__pageRenderProtectionBootstrap?.tasks.get(envelope.payload)
  return task ? await task : null
}

export function isProtectedPageRenderData(value: unknown): value is ProtectedPageRenderData {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return false
  const envelope = (value as ProtectedPageRenderData)[PROTECTION_FIELD]
  return Boolean(
    envelope
    && envelope.version === PROTECTION_VERSION
    && envelope.encoding === PROTECTION_ENCODING
    && envelope.payload,
  )
}

export function usePageRenderProtectionState(): Readonly<Ref<boolean>> {
  return inject(PAGE_RENDER_PROTECTION_STATE_KEY, computed(() => false))
}

/**
 * Replaces display copy/media with a same-shape SSR skeleton and stores the
 * original data as Gzip + Base64. This keeps copy and media URLs out of raw
 * HTML for no-index pages without pretending to be a security boundary: any
 * client that executes the application can restore the original data.
 */
export async function protectPageRenderDataForSsr<T extends Record<string, unknown>>(
  data: T,
  enabled: boolean,
): Promise<T | ProtectedPageRenderData<T>> {
  if (!enabled || import.meta.client || !data)
    return data

  const skeleton = createPageRenderSkeleton(data)
  const envelope = encodePageRenderData(data)

  return {
    ...skeleton,
    [PROTECTION_FIELD]: envelope,
  }
}

export async function restoreProtectedPageRenderData<T>(value: T): Promise<T> {
  if (!isProtectedPageRenderData(value))
    return value

  const envelope = value[PROTECTION_FIELD]!
  const earlyResult = await restoreFromEarlyBootstrap(envelope)
  if (earlyResult?.ok)
    return earlyResult.value as T

  return decodePageRenderEnvelope<T>(envelope)
}

/**
 * Keeps the protected SSR tree for hydration, then swaps the whole PageRender
 * data object exactly once after decryption. renderRevision lets PageRender
 * remount its inner component tree atomically so components with local SSR
 * caches do not remain stuck on placeholder values.
 */
export function useProtectedPageRenderData<T>(source: () => T | null | undefined): ProtectedPageRenderState<T> {
  const data = shallowRef<T | null | undefined>(source())
  const renderRevision = ref(0)
  const isProtected = computed(() => isProtectedPageRenderData(data.value))
  let mounted = false
  let restoreToken = 0

  async function restore(value: T | null | undefined) {
    if (!isProtectedPageRenderData(value))
      return

    const token = ++restoreToken
    try {
      const restored = await restoreProtectedPageRenderData(value)
      if (token !== restoreToken)
        return
      data.value = restored
      renderRevision.value += 1
    }
    catch (error) {
      console.error('[PageRender] Failed to restore protected render data:', error)
    }
  }

  watch(
    source,
    (value) => {
      restoreToken += 1
      data.value = value
      renderRevision.value += 1
      if (mounted)
        void restore(value)
    },
    { flush: 'sync' },
  )

  onMounted(() => {
    mounted = true
    void restore(data.value)
  })

  return {
    data,
    isProtected,
    renderRevision,
  }
}
